import { useQuery } from "@tanstack/react-query";

const DEFAULT = { lat: 13.7563, lon: 100.5018 } as const;

export type WeatherData = {
  current: {
    temperature_2m: number;
    apparent_temperature: number;
    relative_humidity_2m: number;
    wind_speed_10m: number;
    precipitation: number;
    weather_code: number;
    time: string;
  };
  hourly: {
    time: string[];
    temperature_2m: number[];
    precipitation_probability: number[];
    precipitation: number[];
    relative_humidity_2m: number[];
    wind_speed_10m: number[];
  };
  daily: {
    time: string[];
    temperature_2m_max: number[];
    temperature_2m_min: number[];
    precipitation_sum: number[];
    sunrise: string[];
    sunset: string[];
    weather_code: number[];
  };
};

export function useWeather(lat?: number, lon?: number, timeZone?: string) {
  return useQuery<WeatherData>({
    queryKey: [
      "weather",
      lat ?? DEFAULT.lat,
      lon ?? DEFAULT.lon,
      timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
    ],
    queryFn: async () => {
      const url = new URL("https://api.open-meteo.com/v1/forecast");
      const la = lat ?? DEFAULT.lat;
      const lo = lon ?? DEFAULT.lon;
      const tz = timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
      url.searchParams.set("latitude", String(la));
      url.searchParams.set("longitude", String(lo));
      url.searchParams.set(
        "current",
        [
          "temperature_2m",
          "apparent_temperature",
          "relative_humidity_2m",
          "wind_speed_10m",
          "precipitation",
          "weather_code",
        ].join(",")
      );
      url.searchParams.set(
        "hourly",
        [
          "temperature_2m",
          "precipitation_probability",
          "precipitation",
          "relative_humidity_2m",
          "wind_speed_10m",
        ].join(",")
      );
      url.searchParams.set(
        "daily",
        [
          "temperature_2m_max",
          "temperature_2m_min",
          "precipitation_sum",
          "sunrise",
          "sunset",
          "weather_code",
        ].join(",")
      );
      url.searchParams.set("timezone", tz);
      url.searchParams.set("forecast_days", "7");

      const res = await fetch(url.toString());
      if (!res.ok) throw new Error("Failed to fetch weather");
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
  });
}

export type AirQualityData = {
  hourly: {
    time: string[];
    pm2_5: number[];
    pm10: number[];
    european_aqi: number[];
  };
};

export function useAirQuality(lat?: number, lon?: number, timeZone?: string) {
  return useQuery<AirQualityData>({
    queryKey: [
      "air-quality",
      lat ?? DEFAULT.lat,
      lon ?? DEFAULT.lon,
      timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
    ],
    queryFn: async () => {
      const url = new URL("https://air-quality-api.open-meteo.com/v1/air-quality");
      const la = lat ?? DEFAULT.lat;
      const lo = lon ?? DEFAULT.lon;
      const tz = timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
      url.searchParams.set("latitude", String(la));
      url.searchParams.set("longitude", String(lo));
      url.searchParams.set("hourly", ["pm2_5", "pm10", "european_aqi"].join(","));
      url.searchParams.set("timezone", tz);
      const res = await fetch(url.toString());
      if (!res.ok) throw new Error("Failed to fetch air quality");
      return res.json();
    },
    staleTime: 10 * 60 * 1000,
  });
}
